const targetSelector = 'product-card, quick-add-component, .section-resource-list, product-recommendations';
const quickAddClickSelector = 'quick-add-component [on\\:click^="quick-add-component/"], quick-add-component button[name="add"]';
const hoverCardSelector = '.sp-home__product-card';
const hoverMediaSelector = '.sp-home__product-media--has-hover';
const hoverSourceSelector = 'source[data-srcset]';
const hoverImageSelector = '.sp-home__product-image--hover';
const hoverReadyClass = 'sp-home__product-media--hover-ready';
const intentEvents = ['pointerover', 'focusin', 'touchstart'];
const canPrimeHoverImages =
  typeof window.matchMedia !== 'function' || window.matchMedia('(hover: hover) and (pointer: fine)').matches;

let loadPromise;

function loadProductCardScripts() {
  if (loadPromise) return loadPromise;

  document.documentElement.dataset.spProductCardScripts = 'loading';

  loadPromise = Promise.allSettled([
    import('@theme/product-card'),
    import('@theme/quick-add'),
    import('@theme/product-form'),
    import('@theme/product-price'),
    import('@theme/component-quantity-selector'),
    import('@theme/media'),
    import('@theme/slideshow'),
    import('@theme/media-gallery'),
  ])
    .then((results) => {
      const failed = results.find((result) => result.status === 'rejected');

      if (failed) {
        throw failed.reason;
      }

      return Promise.all([
        customElements.whenDefined('product-card'),
        customElements.whenDefined('quick-add-component'),
        customElements.whenDefined('product-form-component'),
        customElements.whenDefined('product-price'),
        customElements.whenDefined('add-to-cart-component'),
        customElements.whenDefined('quantity-selector-component'),
        customElements.whenDefined('deferred-media'),
        customElements.whenDefined('product-model'),
        customElements.whenDefined('slideshow-component'),
        customElements.whenDefined('media-gallery'),
      ]);
    })
    .then(() => {
      document.documentElement.dataset.spProductCardScripts = 'loaded';
    })
    .catch((error) => {
      document.documentElement.dataset.spProductCardScripts = 'error';
      loadPromise = undefined;
      throw error;
    });

  return loadPromise;
}

function warnLoadFailure(error) {
  console.warn('[Starryve] Product card interactions failed to load.', error);
}

function scriptsAreReady() {
  return (
    customElements.get('product-card') &&
    customElements.get('quick-add-component') &&
    customElements.get('product-form-component') &&
    customElements.get('product-price') &&
    customElements.get('add-to-cart-component') &&
    customElements.get('quantity-selector-component') &&
    customElements.get('deferred-media') &&
    customElements.get('product-model') &&
    customElements.get('slideshow-component') &&
    customElements.get('media-gallery')
  );
}

function findQuickAddClickTrigger(event) {
  const target = event.target;

  return target instanceof Element ? target.closest(quickAddClickSelector) : null;
}

function replayClick(trigger) {
  window.requestAnimationFrame(() => {
    trigger.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));
  });
}

function openProductPage(trigger) {
  const quickAdd = trigger.closest('quick-add-component');
  const productUrl = quickAdd?.dataset.productUrl || trigger.closest('product-card')?.querySelector('a[href]')?.href;

  if (productUrl) window.location.href = productUrl;
}

function loadHoverImage(media) {
  const source = media.querySelector(hoverSourceSelector);
  const image = media.querySelector(hoverImageSelector);

  if (!(source instanceof HTMLSourceElement) || !(image instanceof HTMLImageElement)) return;

  if (!source.srcset) source.srcset = source.dataset.srcset || '';

  if (image.complete && image.naturalWidth > 1) {
    media.classList.add(hoverReadyClass);
    return;
  }

  image.addEventListener('load', () => media.classList.add(hoverReadyClass), { once: true });
}

function handleHoverImageIntent(event) {
  if (!(event.target instanceof Element)) return;

  const card = event.target.closest(hoverCardSelector);
  const media = card?.querySelector(hoverMediaSelector);

  if (media instanceof HTMLElement) loadHoverImage(media);
}

function handleIntent(event) {
  if (!(event.target instanceof Element)) return;
  if (!event.target.closest(targetSelector)) return;

  for (const eventName of intentEvents) {
    document.removeEventListener(eventName, handleIntent, { capture: true });
  }

  loadProductCardScripts().catch(warnLoadFailure);
}

function observeDeferredProductCards() {
  const targets = document.querySelectorAll(targetSelector);
  if (!targets.length) return;

  for (const eventName of intentEvents) {
    document.addEventListener(eventName, handleIntent, { capture: true, passive: true });
  }

  if (!('IntersectionObserver' in window)) {
    loadProductCardScripts().catch(warnLoadFailure);
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;

      observer.disconnect();
      loadProductCardScripts().catch(warnLoadFailure);
    },
    { rootMargin: '320px 0px' }
  );

  targets.forEach((target) => observer.observe(target));
}

if (canPrimeHoverImages) {
  document.addEventListener('pointerover', handleHoverImageIntent, { capture: true, passive: true });
  document.addEventListener('focusin', handleHoverImageIntent, { capture: true });
}

window.addEventListener(
  'click',
  (event) => {
    const trigger = findQuickAddClickTrigger(event);

    if (!trigger || scriptsAreReady()) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    loadProductCardScripts()
      .then(() => replayClick(trigger))
      .catch((error) => {
        warnLoadFailure(error);
        openProductPage(trigger);
      });
  },
  { capture: true }
);

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', observeDeferredProductCards, { once: true });
} else {
  observeDeferredProductCards();
}
